import { createUseStyles } from 'react-jss';
import { Breakpoints } from '@/lib/breakpoints';
import { HEADER_HEIGHT, BREADCRUMB_HEIGHT, PAGE_GUTTER, PAGE_GUTTER_X, PAGE_GUTTER_BOTTOM } from '@/constants/layout';
import { BackgroundColors, TextColors } from '@picsart/design-system/foundation/colors';

const useStyles = createUseStyles(
  {
    main: {
      minHeight: '100vh',
      /**
       * An app shell from MD up: a fixed-height flex column that fills what the
       * header leaves. The document's own overflow is handled declaratively —
       * see the `main[data-app-shell]` rule in `globals.css`.
       *
       * The embed below sizes by *flex*, not by `100vh` arithmetic, so the
       * breadcrumb row and the gutters are subtracted by the layout rather than
       * by a list of constants that all have to agree.
       */
      [Breakpoints.MD]: {
        minHeight: 0,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
      backgroundColor: BackgroundColors.base,
      color: TextColors.base,
    },
    /**
     * Pinned under the header, at a fixed height.
     *
     * Sticky rather than scrolling away, so the space above the embed is
     * constant — the embed is the viewport minus that space, and a band that
     * disappeared on scroll would leave it that much too tall.
     *
     * The height is fixed for the same reason, with the trail scrolling
     * horizontally inside it: a long trail wrapping to a second line would
     * change the constant out from under everything below.
     */
    crumbWrap: {
      position: 'sticky',
      top: HEADER_HEIGHT,
      // Static on desktop: the page does not scroll there, so there is nothing
      // to pin against — and pinning inside `main` (now its own scrollport)
      // offset the row by the header's height a second time, dropping it on top
      // of the embed.
      [Breakpoints.MD]: { position: 'static', flexShrink: 0 },
      // Under the header (50), above the miniapp's own sticky chrome.
      zIndex: 46,
      display: 'flex',
      alignItems: 'center',
      // See the header: a declared height plus padding needs `border-box`, or
      // the row is taller than the constant everything below it pins against.
      boxSizing: 'border-box',
      height: BREADCRUMB_HEIGHT,
      // Aligned with the embed below, so the trail starts level with it.
      paddingLeft: PAGE_GUTTER_X,
      paddingRight: PAGE_GUTTER_X,
      backgroundColor: BackgroundColors.base,
      overflowX: 'auto',
      whiteSpace: 'nowrap',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    /**
     * Full-bleed, with only enough padding to keep the embed off the edge.
     *
     * Not capped and centred: the miniapp is an app shell and draws a masonry
     * grid of its own, so every pixel spent on empty gutters is a column it
     * cannot draw — on a wide monitor that was most of the screen left black.
     */
    body: {
      // Tight top and bottom — the embed's height is measured against the top
      // gutter, so every pixel there comes off it — and a little more at the
      // sides, where it only keeps content off the window edge.
      padding: `${PAGE_GUTTER}px ${PAGE_GUTTER_X}px ${PAGE_GUTTER_BOTTOM}px`,
      [Breakpoints.MD]: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
    },
  },
  { name: 'preset-layout' },
);

export default useStyles;
